import React, { useState, useEffect } from "react";
import "../AddAdvertisement.css";
import KeywordSelectionModal from "../components/KeywordSelectionModal";
import WeekdayOnlyDatePicker from "../components/WeekdayOnlyDatePicker";
import { getNaverShoppingRankAPI } from "../../../../../api";
import { isSpecialUser } from "../../../../../utils/environment";

const ShoppingVisitPage = ({
  formData,
  onInputChange,
  user,
  onNext,
  endSubmitTime,
  step = 1,
  keywordRanks = [],
  addKeywordRank,
  removeKeywordRank,
  updateKeywordRank,
  onKeywordRanksChange,
  zoomSettings,
}) => {
  const [currentStep, setCurrentStep] = useState(step); // 1: 폼 입력, 2: 주문 확인, 3: 완료
  const [urlError, setUrlError] = useState("");
  const [isKeywordModalOpen, setIsKeywordModalOpen] = useState(false);
  const [keywordLoadingIndex, setKeywordLoadingIndex] = useState(null); // 키워드 순위 조회 중인 인덱스

  // step prop이 변경될 때 currentStep 업데이트
  useEffect(() => {
    setCurrentStep(step);
  }, [step]);

  // currentStep이 2가 되면 자동으로 키워드 모달 열기
  useEffect(() => {
    if (currentStep === 2) {
      setIsKeywordModalOpen(true);
    }
  }, [currentStep]);

  // adStartDate와 adEndDate가 변경될 때 광고 기간 자동 계산
  useEffect(() => {
    if (formData.adStartDate && formData.adEndDate) {
      const startDate = new Date(formData.adStartDate);
      const endDate = new Date(formData.adEndDate);

      if (endDate >= startDate) {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // 시작일과 종료일 포함

        // console.log("📅 useEffect로 광고 기간 계산:", {
        //   startDate: formData.adStartDate,
        //   endDate: formData.adEndDate,
        //   timeDiff: timeDiff,
        //   daysDiff: daysDiff,
        //   explanation: `${formData.adStartDate}부터 ${formData.adEndDate}까지 ${daysDiff}일 (시작일과 종료일 모두 포함)`,
        // });

        onInputChange("adDuration", daysDiff.toString());
      }
    }
  }, [formData.adStartDate, formData.adEndDate]);

  // 내일 날짜 계산 (한국 시간 기준)
  const getTomorrowDate = () => {
    const now = new Date();
    const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000); // UTC+9 (한국 시간)
    const tomorrow = new Date(koreaTime);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  // 오늘 날짜 계산 (한국 시간 기준)
  const getTodayDate = () => {
    const now = new Date();
    const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000); // UTC+9 (한국 시간)
    return koreaTime.toISOString().split("T")[0];
  };

  // 최소 날짜 (내일)
  const minDate = getTomorrowDate();
  const todayDate = getTodayDate();

  // 특정 유저는 오늘부터, 일반 유저는 내일부터
  const userIsSpecial = isSpecialUser(user?.id);
  const effectiveMinDate = userIsSpecial ? todayDate : minDate;

  // 광고 종료일의 최소 날짜 (광고 시작일이 있으면 그 날짜, 없으면 내일)
  const getEndDateMin = () => {
    if (formData.adStartDate) {
      return formData.adStartDate;
    }
    return effectiveMinDate;
  };

  // 날짜 검증 함수
  const validateDate = (dateString) => {
    if (!dateString) return "날짜를 선택해주세요.";

    const selectedDate = new Date(dateString);
    const tomorrow = new Date(getTomorrowDate());

    // 유효한 날짜인지 확인
    if (isNaN(selectedDate.getTime())) {
      return "올바른 날짜 형식이 아닙니다.";
    }

    // 특정 유저가 아닌 경우에만 내일 이후인지 확인
    if (!userIsSpecial && selectedDate < tomorrow) {
      return "광고 시작일은 내일 이후여야 합니다.";
    }

    return "";
  };

  // 광고 시작일 입력 처리
  const handleAdStartDateChange = (e) => {
    const value = e.target.value;

    // 날짜 검증
    if (value) {
      const error = validateDate(value);
      if (error) {
        // 에러가 있으면 날짜를 초기화
        onInputChange("adStartDate", "");
        alert(error);
        return;
      }
      // 광고 시작일: 주말(토·일) 선택 불가 (VVIP 또는 특정 유저는 주말 가능)
      const selectedDate = new Date(value);
      const dayOfWeek = selectedDate.getDay();
      const allowWeekendByGrade = user?.price_grade?.name === "VVIP";
      const allowWeekend = allowWeekendByGrade || userIsSpecial;

      if ((dayOfWeek === 0 || dayOfWeek === 6) && !allowWeekend) {
        onInputChange("adStartDate", ""); 
        alert("광고 시작일은 평일만 선택 가능합니다.");
        return;
      }
    }

    // 광고 시작일 업데이트
    onInputChange("adStartDate", value);

    // 광고 시작일이 변경되면 모든 계산 재실행
    if (value) {
      // 진행 일수가 있으면 종료일을 다시 계산 (우선순위)
      const duration = parseInt(formData.adDuration) || 0;
      if (duration > 0) {
        const startDate = new Date(value);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + duration - 1); // 시작일 포함이므로 -1
        const endDateString = endDate.toISOString().split("T")[0];
        onInputChange("adEndDate", endDateString);
      } else if (formData.adEndDate) {
        // 종료일이 있으면 진행 일수를 다시 계산
        calculateAdDuration();
      }
    } else {
      // 광고 시작일이 비어있으면 종료일과 진행 일수 초기화
      onInputChange("adEndDate", "");
      onInputChange("adDuration", "");
    }
  };

  // 광고 종료일 입력 처리
  const handleAdEndDateChange = (e) => {
    const value = e.target.value;
    onInputChange("adEndDate", value);

    // 날짜 검증
    if (value) {
      const error = validateDate(value);
      if (error) {
        // 에러가 있으면 날짜를 초기화
        onInputChange("adEndDate", "");
        alert(error);
      }
    }

    // 광고 기간 자동 계산
    calculateAdDuration();
  };

  // 진행 일수 입력 처리
  const handleAdDurationChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, ""); // 숫자만 허용

    if (!formData.adStartDate) {
      alert("광고 시작일을 먼저 선택해주세요.");
      return;
    }

    if (value === "") {
      onInputChange("adDuration", "");
      onInputChange("adEndDate", "");
      return;
    }

    const duration = parseInt(value) || 0;

    if (duration <= 0) {
      alert("진행 일수는 1일 이상이어야 합니다.");
      return;
    }

    // 광고 시작일로부터 진행 일수만큼 더해서 종료일 계산
    const startDate = new Date(formData.adStartDate);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + duration - 1); // 시작일 포함이므로 -1

    const endDateString = endDate.toISOString().split("T")[0];

    onInputChange("adDuration", duration.toString());
    onInputChange("adEndDate", endDateString);
  };

  // 광고 기간 자동 계산 함수
  const calculateAdDuration = () => {
    if (formData.adStartDate && formData.adEndDate) {
      const startDate = new Date(formData.adStartDate);
      const endDate = new Date(formData.adEndDate);

      if (endDate >= startDate) {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // 시작일과 종료일 포함

        // console.log("📅 광고 기간 계산:", {
        //   startDate: formData.adStartDate,
        //   endDate: formData.adEndDate,
        //   timeDiff: timeDiff,
        //   daysDiff: daysDiff,
        //   explanation: `${formData.adStartDate}부터 ${formData.adEndDate}까지 ${daysDiff}일 (시작일과 종료일 모두 포함)`,
        // });

        onInputChange("adDuration", daysDiff.toString());
      }
    }
  };

  // 결제 금액 자동 계산 함수 (1회 단가 소수점 지원)
  const calculatePrice = () => {
    const dailyQuantity = parseInt(formData.dailyQuantity) || 0;
    const adDuration = parseInt(formData.adDuration) || 0;
    const userPriceByOne = parseFloat(formData.userPriceByOne) || 0;

    const totalQuantity = dailyQuantity * adDuration;
    const totalPrice = totalQuantity * userPriceByOne;

    onInputChange("totalQuantity", totalQuantity.toString());
    onInputChange("price", totalPrice.toString());
  };

  // 일일 수량, 광고 기간, 단가가 변경될 때마다 결제 금액 계산
  useEffect(() => {
    calculatePrice();
  }, [formData.dailyQuantity, formData.adDuration, formData.userPriceByOne]);

  // 복합 구동 방식일 때 키워드별 수량 합계 계산
  const calculateKeywordQuantitySum = () => {
    if (formData.productName !== "복합") return 0;

    return keywordRanks
      .filter((item) => item.selected)
      .reduce((sum, item) => {
        const quantity = parseInt(item.quantity) || 0;
        return sum + quantity;
      }, 0);
  };

  // 키워드 수량 변경 핸들러
  const handleKeywordQuantityChange = (actualIndex, value) => {
    const numValue = value.replace(/[^0-9]/g, "");
    updateKeywordRank(actualIndex, "quantity", numValue);
  };

  // 키워드 구동방식 변경 핸들러
  const handleKeywordActionTypeChange = (actualIndex, value) => {
    updateKeywordRank(actualIndex, "actionType", value);
  };

  // URL 포맷팅 함수
  const formatUrl = (url) => {
    if (!url) return "";

    // URL에서 쿼리 파라미터 제거
    let formattedUrl = url.split("?")[0];

    // 마지막 슬래시 제거
    formattedUrl = formattedUrl.replace(/\/$/, "");

    return formattedUrl;
  };

  // URL 검증 함수
  const validateUrl = (url) => {
    if (!url) return "";

    // 일반적인 URL 패턴 검증
    const urlPattern = /^https?:\/\/[^\s/$.?#].[^\s]*$/;

    if (!urlPattern.test(url)) {
      return "올바른 URL 형식이 아닙니다.";
    }

    return "";
  };

  // URL 입력 처리
  const handleUrlChange = (e) => {
    const value = e.target.value;
    onInputChange("productUrl", value);
    setUrlError(""); // 입력 중에는 에러 메시지 제거
  };

  // URL 포커스 아웃 처리
  const handleUrlBlur = (e) => {
    const value = e.target.value;
    if (value) {
      const formattedUrl = formatUrl(value);
      const error = validateUrl(formattedUrl);

      if (error) {
        setUrlError(error);
      } else {
        onInputChange("productUrl", formattedUrl);
        setUrlError("");
      }
    }
  };

  // 키워드 순위 조회 API 호출 함수
  const fetchKeywordRank = async (keyword, index) => {
    if (!keyword || !keyword.trim() || !formData.productId) {
      return;
    }

    // 로딩 상태 시작
    setKeywordLoadingIndex(index);

    try {
      const rankData = {
        sub: [
          {
            productId: formData.productId,
            name: keyword.trim(),
            pageSize: 1,
          },
        ],
      };

      const response = await getNaverShoppingRankAPI(rankData);
      const subResult = response.data?.sub?.[0];

      if (subResult) {
        const rank = subResult.rank || 999;

        // 선택된 키워드들 중에서 해당 인덱스의 키워드를 업데이트
        const selectedKeywords = keywordRanks.filter((item) => item.selected);
        const actualIndex = keywordRanks.findIndex(
          (item) => item.selected && selectedKeywords[index] === item
        );

        if (actualIndex !== -1) {
          updateKeywordRank(actualIndex, "rank", rank);
        }
      }
    } catch (error) {
      console.error("키워드 순위 조회 실패:", error);
    } finally {
      // 로딩 상태 종료
      setKeywordLoadingIndex(null);
    }
  };

  // 키워드 입력 필드 포커스 아웃 처리
  const handleKeywordBlur = (keyword, index) => {
    const trimmedKeyword = keyword.trim();

    // 빈 문자열이 아니고 순위가 0인 경우에만 API 호출
    if (trimmedKeyword && trimmedKeyword !== "") {
      fetchKeywordRank(trimmedKeyword, index);
    }
  };

  // 키워드 입력 필드 키 다운 처리 (엔터 키)
  const handleKeywordKeyDown = (e, keyword, index, actualIndex) => {
    if (e.key === "Enter") {
      e.preventDefault(); // 기본 엔터 동작 방지
      const trimmedKeyword = keyword.trim();

      // 먼저 trim된 값으로 입력 필드 업데이트
      if (keyword !== trimmedKeyword) {
        updateKeywordRank(actualIndex, "keyword", trimmedKeyword);
      }

      // 중복 검사 (대소문자 구분 없이, trim된 값으로 비교)
      if (trimmedKeyword !== "") {
        // keywordRanks 배열에서 직접 중복 키워드 찾기
        const duplicateActualIndex = keywordRanks.findIndex(
          (item, idx) =>
            idx !== actualIndex &&
            item.keyword.trim() !== "" &&
            item.keyword.trim().toLowerCase() === trimmedKeyword.toLowerCase()
        );

        if (duplicateActualIndex !== -1) {
          const duplicateKeyword = keywordRanks[duplicateActualIndex];

          // 중복 키워드가 이미 selected: true인 경우 팝업 표시
          if (duplicateKeyword.selected) {
            alert("이미 등록된 키워드입니다.");
            // 중복이면 현재 입력 필드 제거
            removeKeywordRank(actualIndex);
            return;
          }

          // 중복 키워드가 selected: false인 경우 true로 변경
          const updatedDuplicateKeyword = {
            ...duplicateKeyword,
            selected: true,
          };

          // 키워드를 배열에서 제거하고 맨 뒤에 추가
          const newKeywordRanks = [...keywordRanks];

          // 중복 키워드가 이미 맨 뒤에 있으면 이동할 필요 없음
          const isLastIndex = duplicateActualIndex === keywordRanks.length - 1;

          if (!isLastIndex) {
            // 중복 키워드 제거 (인덱스가 큰 것부터 제거해야 인덱스가 꼬이지 않음)
            const indicesToRemove = [duplicateActualIndex, actualIndex].sort(
              (a, b) => b - a
            );
            indicesToRemove.forEach((idx) => {
              newKeywordRanks.splice(idx, 1);
            });
            newKeywordRanks.push(updatedDuplicateKeyword); // 맨 뒤에 추가
          } else {
            // 이미 맨 뒤에 있으면 selected만 변경하고 현재 입력 필드만 제거
            newKeywordRanks[duplicateActualIndex] = updatedDuplicateKeyword;
            newKeywordRanks.splice(actualIndex, 1);
          }

          // 부모 컴포넌트에 업데이트된 배열 전달 (한 번에 모든 변경사항 반영)
          if (onKeywordRanksChange) {
            onKeywordRanksChange(newKeywordRanks);
          }
          return;
        }
      }

      // 빈 문자열이 아니고 순위가 0인 경우에만 API 호출
      if (trimmedKeyword && trimmedKeyword !== "") {
        fetchKeywordRank(trimmedKeyword, index);
      }

      // 포커스 해제
      e.target.blur();
    }
  };

  // 키워드 입력 변경 핸들러 (입력 중에는 원본 유지, blur/Enter 시 trim + 중복 검사)
  const handleKeywordChange = (e, actualIndex) => {
    const inputValue = e.target.value;
    // 입력 중에는 원본 값 유지 (사용자가 입력하는 동안 공백을 볼 수 있도록)
    updateKeywordRank(actualIndex, "keyword", inputValue);
  };

  // 키워드 blur 핸들러 (trim + 중복 검사)
  const handleKeywordBlurWithValidation = (keyword, index, actualIndex) => {
    const trimmedKeyword = keyword.trim();

    // 먼저 trim된 값으로 입력 필드 업데이트
    if (keyword !== trimmedKeyword) {
      updateKeywordRank(actualIndex, "keyword", trimmedKeyword);
    }

    // 중복 검사 (대소문자 구분 없이, trim된 값으로 비교)
    if (trimmedKeyword !== "") {
      // keywordRanks 배열에서 직접 중복 키워드 찾기
      const duplicateActualIndex = keywordRanks.findIndex(
        (item, idx) =>
          idx !== actualIndex &&
          item.keyword.trim() !== "" &&
          item.keyword.trim().toLowerCase() === trimmedKeyword.toLowerCase()
      );

      if (duplicateActualIndex !== -1) {
        const duplicateKeyword = keywordRanks[duplicateActualIndex];

        // 중복 키워드가 이미 selected: true인 경우 팝업 표시
        if (duplicateKeyword.selected) {
          alert("이미 등록된 키워드입니다.");
          // 중복이면 현재 입력 필드 제거
          removeKeywordRank(actualIndex);
          return;
        }

        // 중복 키워드가 selected: false인 경우 true로 변경하고 맨 뒤로 이동
        const updatedDuplicateKeyword = { ...duplicateKeyword, selected: true };

        // 키워드를 배열에서 제거하고 맨 뒤에 추가
        const newKeywordRanks = [...keywordRanks];

        // 중복 키워드 제거 (인덱스가 큰 것부터 제거해야 인덱스가 꼬이지 않음)
        const indicesToRemove = [duplicateActualIndex, actualIndex].sort(
          (a, b) => b - a
        );
        indicesToRemove.forEach((idx) => {
          newKeywordRanks.splice(idx, 1);
        });

        newKeywordRanks.push(updatedDuplicateKeyword); // 맨 뒤에 추가

        // 부모 컴포넌트에 업데이트된 배열 전달 (한 번에 모든 변경사항 반영)
        if (onKeywordRanksChange) {
          onKeywordRanksChange(newKeywordRanks);
        }
        return;
      }
    }

    // 기존 blur 핸들러 호출 (순위 조회)
    handleKeywordBlur(trimmedKeyword, index);
  };

  const handleSubmit = () => {
    if (!user?.id) {
      onNext();
      return;
    }
    setCurrentStep(2);
  };

  const handleBackFromConfirmation = () => {
    setCurrentStep(1);
  };

  const handleConfirmOrder = () => {
    setCurrentStep(3);
  };

  // 키워드 모달 핸들러들
  const handleOpenKeywordModal = () => {
    setIsKeywordModalOpen(true);
  };

  const handleCloseKeywordModal = () => {
    setIsKeywordModalOpen(false);
  };

  const handleKeywordRanksChange = (newKeywordRanks) => {
    // 부모 컴포넌트의 keywordRanks를 업데이트
    if (onKeywordRanksChange) {
      onKeywordRanksChange(newKeywordRanks);
    }
  };

  // 주문 확인 화면
  if (currentStep === 2) {
    return (
      <div className="shopping-visit-confirmation">
        {/* 빈 공간 */}
        <div className="spacer"></div>
        {/* 1. 상품 정보 입력 섹션 */}
        <div className="product-info-section">
          <div className="product-image-container">
            <div className="product-image">
              {formData.imageUrl ? (
                <img
                  src={formData.imageUrl}
                  alt="상품 이미지"
                  className="product-image-display"
                />
              ) : (
                <div className="image-placeholder">
                  <span>상품 이미지</span>
                </div>
              )}
            </div>
          </div>

          <div className="product-info-grid">
            {/* 첫 번째 줄 */}
            {/* <div className="info-row">
              <div className="form-field">
                <label>카테고리 상품 URL</label>
                <input
                  type="text"
                  value={formData.categoryUrl || ""}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>카테고리 상품명</label>
                <input type="text" value="" readOnly />
              </div>
            </div> */}

            {/* 두 번째 줄 */}
            <div className="info-row">
              <div className="form-field">
                <label>상품 URL 주소</label>
                <input type="text" value={formData.imageUrl || ""} readOnly />
              </div>
              <div className="form-field">
                <label>상품명</label>
                <input
                  type="text"
                  value={formData.missionName || ""}
                  readOnly
                />
              </div>
            </div>

            {/* 세 번째 줄 */}
            <div className="info-row">
              <div className="form-field">
                <label>스토어명</label>
                <input type="text" value={formData.storeName || ""} readOnly />
              </div>
              <div className="form-field">
                <label>메인키워드(순위체크용)</label>
                <input
                  type="text"
                  value={formData.mainKeyword?.name || ""}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>상품번호</label>
                <input type="text" value={formData.productId || ""} readOnly />
              </div>
              <div className="form-field">
                <label>상품 이미지 주소</label>
                <input
                  type="text"
                  value={formData.imageUrl || ""}
                  placeholder="https://shopping..."
                  readOnly
                />
              </div>
            </div>

            {/* 네 번째 줄 */}
            <div className="info-row">
              <div className="form-field">
                <label>일일 수량</label>
                <input
                  type="text"
                  value={`${formData.dailyQuantity || 0}회`}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>광고 기간</label>
                <input
                  type="text"
                  value={`${formData.adDuration || 0}일`}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>광고 시작일</label>
                <input
                  type="text"
                  value={formData.adStartDate || ""}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>

        {/* 2. 키워드 자동 완성 섹션 */}
        <div className="keyword-section">
          <button
            className="keyword-autocomplete-btn"
            onClick={handleOpenKeywordModal}
          >
            유입키워드 자동완성
          </button>

          <div className="keyword-boxes">
            {/* 선택된 키워드들만 표시 */}
            {keywordRanks
              .filter((item) => item.selected)
              .map((item, index) => {
                // 선택된 키워드들 중에서 실제 인덱스 찾기
                const selectedKeywords = keywordRanks.filter(
                  (item) => item.selected
                );
                const actualIndex = keywordRanks.findIndex(
                  (keywordItem) =>
                    keywordItem.selected &&
                    selectedKeywords[index] === keywordItem
                );
                const isComposite = formData.productName === "복합";
                // 특정 유저는 순위 제한 예외
                const effectiveAllowRanking = userIsSpecial
                  ? Infinity
                  : zoomSettings?.allowRanking || 30;

                return (
                  <div
                    key={actualIndex}
                    className={
                      index === 0
                        ? item.rank > effectiveAllowRanking
                          ? "keyword-box error"
                          : item.rank === 0
                          ? "keyword-box empty"
                          : "keyword-box required"
                        : item.rank > effectiveAllowRanking
                        ? "keyword-box error"
                        : item.rank === 0
                        ? "keyword-box empty"
                        : "keyword-box optional"
                    }
                  >
                    <div className="keyword-content">
                      {index === 0 ? (
                        // 첫 번째 항목 (required) - X 버튼 있음
                        <>
                          <div className="keyword-header">
                            <div
                              className={`keyword-rank ${
                                item.rank > effectiveAllowRanking
                                  ? "error"
                                  : item.rank === 0
                                  ? "empty"
                                  : "success"
                              }`}
                            >
                              {item.rank === 999
                                ? "999+위"
                                : item.rank === 0
                                ? "-위"
                                : `${item.rank}위`}
                            </div>
                            <button
                              className={`remove-keyword ${
                                item.rank > effectiveAllowRanking
                                  ? "error"
                                  : ""
                              }`}
                              onClick={() => removeKeywordRank(actualIndex)}
                            >
                              ×
                            </button>
                          </div>
                          <span className="keyword-label">
                            *유입용 키워드{index + 1}(필수)
                          </span>
                          <input
                            type="text"
                            value={item.keyword}
                            onChange={(e) =>
                              handleKeywordChange(e, actualIndex)
                            }
                            onBlur={() =>
                              handleKeywordBlurWithValidation(
                                item.keyword,
                                index,
                                actualIndex
                              )
                            }
                            onKeyDown={(e) =>
                              handleKeywordKeyDown(
                                e,
                                item.keyword,
                                index,
                                actualIndex
                              )
                            }
                            className={`keyword-input-field ${
                              keywordLoadingIndex === index ? "loading" : ""
                            }`}
                            disabled={
                              item.rank !== 0 || keywordLoadingIndex === index
                            } // 순위가 0이 아닌 경우 또는 로딩 중일 때 수정 불가
                          />
                          {keywordLoadingIndex === index && (
                            <div className="keyword-loading">
                              순위 조회 중...
                            </div>
                          )}

                          {/* 복합 구동 방식일 때 구동방식과 수량 입력 */}
                          {isComposite && (
                            <div className="keyword-composite-fields">
                              <div className="keyword-action-type-field">
                                <label>구동방식</label>
                                <select
                                  value={item.actionType || "태그"}
                                  onChange={(e) =>
                                    handleKeywordActionTypeChange(
                                      actualIndex,
                                      e.target.value
                                    )
                                  }
                                  className="keyword-action-type-select"
                                >
                                  <option value="태그">태그</option>
                                  <option value="정답">정답</option>
                                  {/* <option value="리뷰">리뷰</option> */}
                                </select>
                              </div>
                              <div className="keyword-quantity-field">
                                <label>수량</label>
                                <input
                                  type="text"
                                  placeholder="수량 입력"
                                  value={item.quantity || ""}
                                  onChange={(e) =>
                                    handleKeywordQuantityChange(
                                      actualIndex,
                                      e.target.value
                                    )
                                  }
                                  className="keyword-quantity-input"
                                />
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        // 두 번째부터 (optional) - X 버튼 있음
                        <>
                          <div className="keyword-header">
                            <div
                              className={`keyword-rank ${
                                item.rank > effectiveAllowRanking
                                  ? "error"
                                  : item.rank === 0
                                  ? "empty"
                                  : "success"
                              }`}
                            >
                              {item.rank === 999
                                ? "999+위"
                                : item.rank === 0
                                ? "-위"
                                : `${item.rank}위`}
                            </div>
                            <button
                              className={`remove-keyword ${
                                item.rank > effectiveAllowRanking
                                  ? "error"
                                  : ""
                              }`}
                              onClick={() => removeKeywordRank(actualIndex)}
                            >
                              ×
                            </button>
                          </div>
                          <span className="keyword-label">
                            유입용 키워드{index + 1}(선택)
                          </span>
                          <input
                            type="text"
                            value={item.keyword}
                            onChange={(e) =>
                              handleKeywordChange(e, actualIndex)
                            }
                            onBlur={() =>
                              handleKeywordBlurWithValidation(
                                item.keyword,
                                index,
                                actualIndex
                              )
                            }
                            onKeyDown={(e) =>
                              handleKeywordKeyDown(
                                e,
                                item.keyword,
                                index,
                                actualIndex
                              )
                            }
                            className={`keyword-input-field ${
                              keywordLoadingIndex === index ? "loading" : ""
                            }`}
                            disabled={
                              item.rank !== 0 || keywordLoadingIndex === index
                            } // 순위가 0이 아닌 경우 또는 로딩 중일 때 수정 불가
                          />
                          {keywordLoadingIndex === index && (
                            <div className="keyword-loading">
                              순위 조회 중...
                            </div>
                          )}

                          {/* 복합 구동 방식일 때 구동방식과 수량 입력 */}
                          {isComposite && (
                            <div className="keyword-composite-fields">
                              <div className="keyword-action-type-field">
                                <label>구동방식</label>
                                <select
                                  value={item.actionType || "태그"}
                                  onChange={(e) =>
                                    handleKeywordActionTypeChange(
                                      actualIndex,
                                      e.target.value
                                    )
                                  }
                                  className="keyword-action-type-select"
                                >
                                  <option value="태그">태그</option>
                                  <option value="정답">정답</option>
                                  {/* <option value="리뷰">리뷰</option> */}
                                </select>
                              </div>
                              <div className="keyword-quantity-field">
                                <label>수량</label>
                                <input
                                  type="text"
                                  placeholder="수량 입력"
                                  value={item.quantity || ""}
                                  onChange={(e) =>
                                    handleKeywordQuantityChange(
                                      actualIndex,
                                      e.target.value
                                    )
                                  }
                                  className="keyword-quantity-input"
                                />
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

            {keywordRanks.filter((item) => item.selected).length < 10 && (
              <button className="add-keyword-btn" onClick={addKeywordRank}>
                +
              </button>
            )}
          </div>

          {/* 복합 구동 방식일 때 수량 합계 표시 */}
          {formData.productName === "복합" && (
            <div className="keyword-quantity-summary">
              <div className="quantity-summary-row">
                <span className="quantity-summary-label">
                  키워드별 수량 합계:
                </span>
                <span className="quantity-summary-value">
                  {calculateKeywordQuantitySum()}회
                </span>
              </div>
              <div className="quantity-summary-row">
                <span className="quantity-summary-label">일일 수량:</span>
                <span className="quantity-summary-value">
                  {formData.dailyQuantity || 0}회
                </span>
              </div>
              {calculateKeywordQuantitySum() !==
                parseInt(formData.dailyQuantity || 0) && (
                <div className="quantity-warning">
                  ⚠️ 키워드별 수량 합계가 일일 수량과 일치하지 않습니다.
                </div>
              )}
            </div>
          )}
        </div>

        {/* 3. 가격 및 수량 정보 섹션 */}
        <div className="pricing-section">
          <div className="pricing-grid">
            <div className="form-field">
              <label>1회 단가</label>
              <input
                type="text"
                value={`${(parseFloat(formData.userPriceByOne) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
                readOnly
              />
            </div>

            <div className="form-field">
              <label>신청 수량</label>
              <input
                type="text"
                value={`${formData.totalQuantity || 0}회`}
                readOnly
              />
            </div>

            <div className="form-field">
              <label>결제 금액</label>
              <input
                type="text"
                value={`${(parseFloat(formData.price) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
                readOnly
              />
            </div>

            <div className="form-field">
              <label>광고 마감일(예정일)</label>
              <input
                type="text"
                value={
                  formData.endDate
                    ? formData.endDate.slice(2, 10).replace(/-/g, "-")
                    : ""
                }
                readOnly
              />
            </div>
          </div>
        </div>

        {/* 키워드 선택 모달 */}
        <KeywordSelectionModal
          isOpen={isKeywordModalOpen}
          onClose={handleCloseKeywordModal}
          keywordRanks={keywordRanks}
          onKeywordRanksChange={handleKeywordRanksChange}
          zoomSettings={zoomSettings}
          userIsSpecial={userIsSpecial}
        />
      </div>
    );
  }

  // 완료 화면
  if (currentStep === 3) {
    return (
      <div className="advertisement-complete">
        <div className="complete-content">
          <div className="complete-icon">✅</div>
          <h2 className="complete-title">광고 등록이 완료되었습니다</h2>
          <p className="complete-description">
            등록하신 광고는 관리자 검토 후 승인됩니다.
            <br />
            승인 상태는 내 광고 확인에서 확인하실 수 있습니다.
          </p>
          <div className="complete-actions">
            <button
              className="complete-btn primary"
              onClick={() => onNext("myAdvertisement")}
            >
              내 광고 확인
            </button>
            <button
              className="complete-btn secondary"
              onClick={() => setCurrentStep(1)}
            >
              새 광고 등록
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shopping-visit-page">
      {/* <div className="page-description">
        <h3>네이버 쇼핑 방문 광고</h3>
        <p>상품 페이지를 방문하여 조회수를 높이는 광고 서비스입니다.</p>
      </div> */}

      <div className="add-form-fields">
        {/* 첫 번째 줄: 미션 구동 방식 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>미션 구동 방식</label>
            <select
              value={formData.productName || ""}
              onChange={(e) => onInputChange("productName", e.target.value)}
              disabled={!user?.id}
              className="product-name-select"
            >
              <option value="태그">태그</option>
              <option value="정답">정답</option>
              {/* <option value="리뷰">리뷰</option> */}
              {/* <option value="복합">복합</option> */}
            </select>
          </div>
        </div>

        {/* 두 번째 줄: 상품 URL 주소와 메인키워드 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>상품 URL 주소</label>
            <input
              type="text"
              placeholder="https://smartstore.naver.com/..."
              value={formData.productUrl}
              onChange={handleUrlChange}
              onBlur={handleUrlBlur}
              disabled={!user?.id}
              className={urlError ? "error" : ""}
            />
            {urlError && <p className="error-message">{urlError}</p>}
          </div>
          <div className="add-form-field">
            <label>메인키워드(순위체크용)</label>
            <input
              type="text"
              placeholder="필수입력"
              value={formData.mainKeyword?.name || ""}
              onChange={(e) => onInputChange("mainKeyword", e.target.value)}
              disabled={!user?.id}
            />
          </div>
        </div>

        {/* 세 번째 줄: 일일 수량 */}
        <div className="add-form-row daily-quantity-row">
          <div className="add-form-field">
            <label>일일 수량</label>
            <input
              type="text"
              placeholder="100의 배수 입력"
              value={formData.dailyQuantity}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                onInputChange("dailyQuantity", value);
              }}
              disabled={!user?.id}
            />
          </div>
        </div>

        {/* 네 번째 줄: 광고 시작일, 진행 일수, 광고 종료일 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>광고 시작일</label>
            <WeekdayOnlyDatePicker
              value={formData.adStartDate}
              onChange={(dateStr) =>
                handleAdStartDateChange({ target: { value: dateStr } })
              }
              minDate={effectiveMinDate}
              disabled={!user?.id}
              userId={user?.id}
            />
          </div>
          <div className="add-form-field">
            <label>진행 일수</label>
            <input
              type="text"
              placeholder="일수 입력"
              value={formData.adDuration || ""}
              onChange={handleAdDurationChange}
              disabled={!user?.id || !formData.adStartDate}
            />
          </div>
          <div className="add-form-field">
            <label>광고 종료일</label>
            <WeekdayOnlyDatePicker
              value={formData.adEndDate}
              onChange={(dateStr) =>
                handleAdEndDateChange({ target: { value: dateStr } })
              }
              minDate={getEndDateMin()}
              disabled={!user?.id || !formData.adStartDate}
              weekdaysOnly={false}
              userId={user?.id}
            />
          </div>
        </div>

        {/* 다섯 번째 줄: 결제 금액 정보 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>1회 단가</label>
            <input
              type="text"
              value={`${(parseFloat(formData.userPriceByOne) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
              readOnly
            />
          </div>
          <div className="add-form-field">
            <label>신청 수량</label>
            <input
              type="text"
              value={`${formData.totalQuantity || 0}회`}
              readOnly
            />
          </div>
          <div className="add-form-field">
            <label>결제 금액</label>
            <input
              type="text"
              value={`${(parseFloat(formData.price) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
              readOnly
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShoppingVisitPage;
